import { action, observable } from 'mobx'
import { validationContext, required } from 'validx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  mustBeValidPersonalTaxpayerIdentificationNumber,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  mustBeInThePast,
} from '@/utils/validx-util'
import type { DependentRelationship } from '@taxfyle/txf-api-client'

export interface AddDependentDialogViewModelOptions {
  onSaved: (dependent: NewDependentData) => void
}

export interface NewDependentData {
  firstName: string
  middleInitial: string
  lastName: string
  tin: string
  dateOfBirth: Date | null
  relationship: DependentRelationship | null
}

export class AddDependentDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor firstName = ''
  @observable accessor middleInitial = ''
  @observable accessor lastName = ''
  @observable accessor tin = ''
  @observable accessor dateOfBirth: Date | null = null
  @observable accessor relationship: DependentRelationship | null = null

  readonly validation = validationContext(this, {
    firstName: [
      required('First name is required'),
      mustBeValidTaxFormInputValue(),
    ],
    lastName: [
      required('Last name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Last name must not exceed 100 characters'),
    ],
    middleInitial: [
      mustHaveMaxLength(1, 'Middle initial must be a single character'),
      mustBeValidTaxFormInputValue(),
    ],
    tin: [
      required('SSN is required'),
      mustBeValidPersonalTaxpayerIdentificationNumber(),
    ],
    dateOfBirth: [
      required('Date of birth is required'),
      mustBeInThePast('Date of birth must be in the past'),
    ],
    relationship: [required('Relationship is required')],
  })

  constructor(private readonly _options: AddDependentDialogViewModelOptions) {}

  @action.bound
  reset() {
    this.firstName = ''
    this.middleInitial = ''
    this.lastName = ''
    this.tin = ''
    this.dateOfBirth = null
    this.relationship = null
    this.validation.reset()
  }

  @action.bound
  showDialog() {
    this.reset()
    this.modal.show()
  }

  @action.bound
  closeDialog() {
    this.modal.close()
  }

  @action.bound
  save() {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    const dependentData: NewDependentData = {
      firstName: this.firstName,
      middleInitial: this.middleInitial,
      lastName: this.lastName,
      tin: this.tin,
      dateOfBirth: this.dateOfBirth,
      relationship: this.relationship,
    }

    this._options.onSaved(dependentData)
    this.closeDialog()
  }
}
